/**
 * F13.6 — Célula monetária reutilizável.
 *
 * Padroniza formatação Intl.NumberFormat fr-FR EUR + cor semântica.
 * Quando `value === 0` e `emptyAs === '—'`, renderiza traço muted.
 *
 * Variantes:
 *   - default : text-text-primary (branco)
 *   - lime    : text-lime — para faturado >= devis (sucesso financeiro)
 *   - muted   : text-text-muted — sem valor ou irrelevante
 *   - success : text-success — para tec_facturado == tec_previsto (verde calmo)
 *   - warning : text-warning — divergência (laranja)
 *   - danger  : text-danger  — negativo / erro
 */

import { toNumber } from "../../lib/format-fr.js";

interface Props {
  value: number | string | null | undefined;
  variant?: "default" | "lime" | "muted" | "success" | "warning" | "danger";
  /** Default '—'. Use '0 €' para sempre mostrar zero formatado. */
  emptyAs?: "—" | "0 €";
  decimals?: 0 | 2;
}

const moneyIntl0 = new Intl.NumberFormat("fr-FR", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const moneyIntl2 = new Intl.NumberFormat("fr-FR", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const COLOR_BY_VARIANT: Record<NonNullable<Props["variant"]>, string> = {
  default: "text-text-primary",
  lime: "text-lime",
  muted: "text-text-muted",
  success: "text-success",
  warning: "text-warning",
  danger: "text-danger",
};

export function MoneyCell({ value, variant = "default", emptyAs = "—", decimals = 0 }: Props) {
  const n = toNumber(value);
  if (n === null || (n === 0 && emptyAs === "—")) {
    return <span className="text-text-muted">—</span>;
  }
  const fmt = decimals === 2 ? moneyIntl2 : moneyIntl0;
  return (
    <span className={`font-display font-bold tabular-nums ${COLOR_BY_VARIANT[variant]}`}>
      {fmt.format(n)}
    </span>
  );
}
